import { Users } from 'lucide-react';
import { useGetEventResponsesQuery } from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const stampFmt = new Intl.DateTimeFormat('en-GH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/*
  Who is coming.

  The four figures at the top are the ones the office acts on: going, not
  going, still to answer, and total heads including the adults coming with
  them. "Awaiting" is deliberately shown as its own number rather than left to
  be worked out from the others — it is the one that turns into phone calls.
*/
export function ResponsesDialog({
  eventId,
  institutionId,
  onClose,
}: {
  eventId: string | null;
  institutionId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetEventResponsesQuery(
    { institutionId, eventId: eventId ?? '' },
    { skip: !eventId },
  );

  return (
    <Modal
      open={eventId !== null}
      onClose={onClose}
      size="lg"
      title="Replies"
      description={data?.title}
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isLoading || !data ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Going', value: data.going, tone: 'text-[hsl(var(--record-ink-success))]' },
              { label: 'Not going', value: data.notGoing, tone: 'text-muted-foreground' },
              { label: 'Awaiting', value: data.awaiting, tone: data.awaiting ? 'text-[hsl(var(--record-ink-warning))]' : '' },
              { label: 'Total heads', value: data.going + data.guests, tone: '' },
            ].map((f) => (
              <div key={f.label} className={cn(styles.panel, 'px-3 py-2')}>
                <p className={styles.label}>{f.label}</p>
                <p className={cn(styles.figure, 'mt-0.5 text-[16px]', f.tone)}>{f.value}</p>
              </div>
            ))}
          </div>

          <p className={styles.meta}>
            {data.responses.length} of {data.audience} children have an answer
            {data.guests > 0 && ` · ${data.guests} adult${data.guests === 1 ? '' : 's'} coming with them`}
          </p>

          {data.responses.length === 0 ? (
            <div className={cn(styles.panel, 'overflow-clip')}>
              <EmptyState
                icon={<Users size={17} />}
                title="No replies yet"
                description="Answers appear here as parents respond in their app."
              />
            </div>
          ) : (
            <div className={cn(styles.panel, 'divide-y divide-border')}>
              {data.responses.map((r) => (
                <div key={r._id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-[11.5px] font-semibold text-foreground">{r.studentName}</p>
                    <p className={styles.meta}>
                      {r.groupName && `${r.groupName} · `}
                      {stampFmt.format(new Date(r.respondedAt))}
                      {r.note && ` · "${r.note}"`}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {r.response === 'going' && r.guestCount > 0 && (
                      <span className={styles.meta}>+{r.guestCount}</span>
                    )}
                    {r.response === 'going' ? (
                      <Mark tone="success">Going</Mark>
                    ) : (
                      <Mark tone="neutral">Not going</Mark>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
